import { BatchGetAssetPropertyValueCommand, } from '@aws-sdk/client-iotsitewise';
import { toDataPoint } from '../util/toDataPoint';
import { dataStreamFromSiteWise } from '../dataStreamFromSiteWise';
import { fromId } from '../util/dataStreamId';
import { isDefined } from '../../common/predicates';
import { createRawLatestEntryBatches, shouldFetchNextBatch, NO_LIMIT_BATCH, } from './batch';
import { deduplicateBatch } from '../util/deduplication';
import { withinLatestPropertyDataThreshold } from './withinLatestPropertyDataThreshold';
/**
 *  This API currently does not paginate and nextToken will always be null. However, once
 *  Hybrid Query (hot/cold) is supported it's possible that the API will not return
 *  all entries in a single API request. Supporting nextToken future-proofs this implementation.
 */
const sendRequest = ({ client, batch, requestIndex, // used to create and regenerate (for paginating) a unique entryId
nextToken: prevToken, }) => {
    // callback cache makes it convenient to capture request data in a closure.
    // the cache exposes methods that only require batch response entry as an argument.
    const callbackCache = {};
    client
        .send(new BatchGetAssetPropertyValueCommand({
        entries: deduplicateBatch(batch).map((entry, entryIndex) => {
            const { requestInformation, onError, onSuccess, requestStart, requestEnd, } = entry;
            const { id } = requestInformation;
            // use 2D array indices as entryIDs to guarantee uniqueness
            // entryId is used to map batch entries with the appropriate callback
            const entryId = String(`${requestIndex}-${entryIndex}`);
            // save request entry data in functional closure.
            callbackCache[entryId] = {
                onError: ({ errorMessage: msg = 'batch latest value error', errorCode: status, }) => {
                    onError({
                        id,
                        resolution: 0,
                        error: { msg, status },
                        aggregationType: requestInformation.aggregationType,
                    });
                },
                onSuccess: ({ assetPropertyValue }) => {
                    if (assetPropertyValue) {
                        const dataStream = dataStreamFromSiteWise({
                            ...fromId(id),
                            dataPoints: [toDataPoint(assetPropertyValue)].filter(isDefined),
                        });
                        onSuccess([dataStream], requestInformation, requestStart, requestEnd);
                    }
                },
            };
            // BatchGetAssetPropertyValueEntry
            return {
                ...fromId(requestInformation.id),
                entryId,
            };
        }),
        nextToken: prevToken,
    }))
        .then((response) => {
        const { errorEntries, successEntries, nextToken } = response;
        // execute the correct callback for each entry
        // empty entries and entries that don't exist in the cache are ignored.
        // TODO: implement retries for retry-able batch errors
        errorEntries === null || errorEntries === void 0 ? void 0 : errorEntries.forEach((entry) => { var _a; return entry.entryId && ((_a = callbackCache[entry.entryId]) === null || _a === void 0 ? void 0 : _a.onError(entry)); });
        successEntries === null || successEntries === void 0 ? void 0 : successEntries.forEach((entry) => { var _a; return entry.entryId && ((_a = callbackCache[entry.entryId]) === null || _a === void 0 ? void 0 : _a.onSuccess(entry)); });
        if (shouldFetchNextBatch({ nextToken, maxResults: NO_LIMIT_BATCH })) {
            sendRequest({
                client,
                batch,
                requestIndex,
                nextToken,
            });
        }
    });
};
const batchGetLatestPropertyDataPointsForProperty = ({ client, entries, }) => createRawLatestEntryBatches(entries)
    .filter((batch) => batch.length > 0) // filter out empty batches
    .map(([batch], requestIndex) => sendRequest({ client, batch, requestIndex }));
export const batchGetLatestPropertyDataPoints = ({ params, client, }) => {
    const entries = [];
    // fan out params into individual entries, handling fetchMostRecentBeforeStart
    params.forEach(({ requestInformations, onSuccess, onError }) => {
        requestInformations
            .filter(({ end, fetchMostRecentBeforeEnd, resolution }) => resolution === '0' &&
            withinLatestPropertyDataThreshold(end) &&
            fetchMostRecentBeforeEnd)
            .forEach((requestInformation) => {
            const { end } = requestInformation;
            entries.push({
                requestInformation,
                onSuccess,
                onError,
                requestStart: new Date(0, 0, 0),
                requestEnd: end,
            });
        });
    });
    // sort entries to ensure earliest data is fetched first because batch API has a property limit
    entries.sort((a, b) => b.requestInformation.start.getTime() -
        a.requestInformation.start.getTime());
    if (entries.length > 0) {
        batchGetLatestPropertyDataPointsForProperty({
            entries,
            client,
        });
    }
};
